import { ArrowBack, Email } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext, FieldArray } from "formik";
import InputField from "../../common/FormFields/InputField";
import { MarinerProfile } from "../../common/MarinerProfile";
import initialValues from "./formValidation/initialValues";
import formData from "./formValidation/formData";
import validationSchema from "./formValidation/validationSchema";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  selectCurrentDispatch,
  selectCurrentProfile,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { usePrompt } from "../../util/usePrompt";
import {
  Evaluation,
  EvaluationScore,
  EvaluationSignature,
  EvaluationStatus,
} from "../../common/Evaluation";
import {
  useAddSignatureMutation,
  useApproveEvaluationMutation,
  useGetEvaluationQuery,
  useRejectEvaluationMutation,
  useSubmitEvaluationMutation,
  useUpdateEvaluationMutation,
  useArchiveEvaluationMutation,
} from "../../redux/api/evaluation";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useRef, useState } from "react";
import EmailModal from "../email/EmailModal";
import { EmailType } from "../../common/EmailDetails";
import { EvaluationSignature as EvaluationSignatureInput } from "./EvaluationSignature";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Vessel } from "../../common/Vessel";
import { JobRating, JobRatingLabel } from "../../common/JobOrder";
import logo from "../.././images/ts-logo-nobg.png";
import EvaluationCommentEditor from "./EvaluationCommentEditor";
import EvaluationAssessorAutocomplete from './EvaluationAssessorAutocomplete';
import SelectField from "../../common/FormFields/SelectField";
import MatrixModal from "./MatrixModal";
import { Dispatch } from "../../common/Dispatch";
import { selectEmailsByVesselAndRating } from "../../redux/vessel/vesselEmailSlice";

interface EvaluationFormProps {
  juniorOfficer?: boolean;
}

const EvaluationForm = (props: EvaluationFormProps) => {
  const navigate = useNavigate();
  const { formId, formField } = formData;
  const {
    firstName,
    middleName,
    lastName,
    mmcId,
    rating,
    vessel,
    fromDate,
    toDate,
    nearMissSubmissions,
    lostTimeInjuries,
    restrictedWorkInjuries,
    medicalTreatmentInjuries,
    scores,
    assessor1,
    assessor1Comment,
    assessor1CommentHelper,
    assessor2,
    assessor2Comment,
    assessor2CommentHelper,
    finalReviewer,
    finalReviewerComment,
    finalReviewerCommentHelper,
    evaluatee,
    evaluateeComment,
    evaluateeCommentHelper,
    status,
    readyForPromotion,
    hasPreviousWarnings,
    reviewedWithMariner,
  } = formField;

  const { id } = useParams();

  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const userProfile: MarinerProfile = useSelector(selectCurrentProfile);
  const currentDispatch: Dispatch = useSelector(selectCurrentDispatch);

  const {
    data: evaluationFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetEvaluationQuery(id !== undefined ? id : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const [updateEvaluation] = useUpdateEvaluationMutation();
  const [submitEvaluation] = useSubmitEvaluationMutation();
  const [approveEvaluation] = useApproveEvaluationMutation();
  const [rejectEvaluation] = useRejectEvaluationMutation();
  const [archiveEvaluation] = useArchiveEvaluationMutation();
  const [submittingEvaluation, setSubmittingEvaluation] = useState(false);
  const [addSignature] = useAddSignatureMutation();

  const [signatureFile, setSignatureFile] = useState("");
  const [signatureText, setSignatureText] = useState("");

  // state variables for evaluation rejection
  const [confirmReject, setConfirmReject] = useState(false);
  const [note, setNote] = useState("");

  // component refs for export
  const formRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);
  const commentHeaderRef = useRef<HTMLDivElement>(null);
  const assessor1SectionRef = useRef<HTMLDivElement>(null);
  const assessor2SectionRef = useRef<HTMLDivElement>(null);
  const finalReviewerSectionRef = useRef<HTMLDivElement>(null);
  const evaluateeSectionRef = useRef<HTMLDivElement>(null);

  const getAssessorRating = (): string => {
    switch (evaluationFromApi?.status) {
      case "EVALUATION_INITIATED":
      case "ASSESSOR_1_EDIT":
      case "ASSESSOR_2_RETURN":
        return evaluationFromApi?.assessor1Title ?? "";
      case "ASSESSOR_1_SUBMIT":
      case "FINAL_REVIEWER_RETURN":
        return evaluationFromApi?.assessor2Title ?? "";
      default:
        return "";
      }
  };
    const ccEmail = useSelector(selectEmailsByVesselAndRating(evaluationFromApi?.vessel?.id ?? 0,getAssessorRating()));

  const handleUploadSignature = async (values: any) => {
    setSubmittingEvaluation(true);
    // TODO: error handling
    if (!id) {
      setSubmittingEvaluation(false);
      return;
    }
    // update the eval with current values
    const saveResult = await updateEvaluation({
      id,
      evaluation: buildUpdatedEvaluation(values),
    });
    if ("error" in saveResult) {
      setSubmittingEvaluation(false);
      return;
    }
    if (!signatureFile && !signatureText) {
      toast.error(<p>A signature is required.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      setSubmittingEvaluation(false);
      return;
    }
    const formData = new FormData();
    if (signatureFile) {
      const signatureBlob = await (await fetch(signatureFile)).blob(); // Convert data URL to Blob
      formData.append("fileSignature", signatureBlob, "signature.png");
    }
    if (signatureText) {
      formData.append("writtenSignature", signatureText);
    }

    const updateResult = await addSignature({ id, file: formData });
    if ("error" in updateResult) {
      setSubmittingEvaluation(false);
      return;
    }
    setSubmittingEvaluation(false);
    refetch();
    toast.success(<p>Evaluation {id} signed successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  let getInitialValues = () => {
    if (!isLoading && !isFetching && id !== undefined && id !== "new") {
      let res: any = JSON.parse(JSON.stringify(evaluationFromApi));
      Object.keys(res || {}).forEach(
        (key) => (res[key] = res[key] === null ? "" : res[key])
      );

      return {
        [firstName.name]: res["profile"]["firstName"],
        [middleName.name]: res["profile"]["middleName"],
        [lastName.name]: res["profile"]["lastName"],
        [mmcId.name]: res["profile"]["mmcId"],
        [rating.name]: res["rating"] || res["profile"]["rating"],
        [vessel.name]: res["vessel"],
        [fromDate.name]: res["fromDate"],
        [toDate.name]: res["toDate"],
        [nearMissSubmissions.name]: res["vessel"]["nearMissSubmissions"],
        [lostTimeInjuries.name]: res["vessel"]["lostTimeInjuries"],
        [restrictedWorkInjuries.name]: res["vessel"]["restrictedWorkInjuries"],
        [medicalTreatmentInjuries.name]:
          res["vessel"]["medicalTreatmentInjuries"],
        [scores.name]: (res["scores"] as EvaluationScore[]).sort(
          (a, b) => (a?.question?.position || 0) - (b?.question?.position || 0)
        ),
        [assessor1.name]: {
          ...res["assessor1"],
          title: res["assessor1Title"]
        },
        [assessor1Comment.name]: res["assessor1Comment"],
        [assessor2.name]: {
          ...res["assessor2"],
          title: res["assessor2Title"]
        },
        [assessor2Comment.name]: res["assessor2Comment"],
        [finalReviewer.name]: {
          ...res["finalReviewer"],
          title: res["finalReviewerTitle"]
        },
        [finalReviewerComment.name]: res["finalReviewerComment"],
        [evaluatee.name]: res["evaluatee"],
        [evaluateeComment.name]: res["evaluateeComment"],
        [status.name]: res["status"],
        [readyForPromotion.name]: res["readyForPromotion"],
        [hasPreviousWarnings.name]: res["hasPreviousWarnings"],
        [reviewedWithMariner.name]: res["reviewedWithMariner"],
        permissions: res["permissions"],
      };
    } else {
      return initialValues;
    }
  };

  const buildUpdatedEvaluation = (values: any) => {
    const isHROrCrewing = (userGroup === UserGroup.HUMAN_RESOURCES || userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
                            userGroup === UserGroup.CREWING_EVAL_SPECIALISTS || userGroup === UserGroup.CREWING_MANAGERS);
    const canUpdateAssessor = props.juniorOfficer ? isHROrCrewing || [JobRating._Master, JobRating._CM, JobRating._CE, JobRating._1AE].includes(currentDispatch?.rating) : isHROrCrewing;

    const evaluation: Evaluation = {
      vessel: values.vessel?.id ? values.vessel : undefined,
      fromDate: values.fromDate,
      toDate: values.toDate,
      lastUpdatedDate: values.lastUpdatedDate,
      scores: values.scores,
      assessor1UserId: canUpdateAssessor
        ? values.assessor1?.id || evaluationFromApi?.assessor1UserId
        : values.assessor1UserId || evaluationFromApi?.assessor1UserId,
      assessor1Comment: values.assessor1Comment,
      assessor2UserId: canUpdateAssessor
        ? values.assessor2?.id || evaluationFromApi?.assessor2UserId
        : values.assessor2UserId || evaluationFromApi?.assessor2UserId,
      assessor2Comment: values.assessor2Comment,
      finalReviewerUserId: isHROrCrewing ? values.finalReviewer.id : values.finalReviewerUserId,
      finalReviewerComment: values.finalReviewerComment,
      evaluateeComment: values.evaluateeComment,
      readyForPromotion: values.readyForPromotion,
      hasPreviousWarnings: values.hasPreviousWarnings,
      reviewedWithMariner: values.reviewedWithMariner,
    };
    return evaluation;
  };

  async function _submitForm(values: any, actions: any) {
    // TODO:
    if (!id) {
      actions.setSubmitting(false);
      return;
    }
    const updateResult = await updateEvaluation({
      id,
      evaluation: buildUpdatedEvaluation(values),
    });
    if ("error" in updateResult) {
      actions.setSubmitting(false);
      return;
    }
    actions.setSubmitting(false);
    refetch();

    toast.success(
      <p>
        Evaluation {!!id && id !== "new" ? "updated" : "created"} successfully.
      </p>,
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      }
    );
  }

  const evaluationStatusIs = (statusList: EvaluationStatus[]) => {
    if (!evaluationFromApi?.status) {
      return false;
    }

    return statusList.includes(evaluationFromApi.status);
  };

  const handleSubmitEvaluation = async (approve?: boolean, values?: any) => {
    setSubmittingEvaluation(true);
    if (!id) {
      setSubmittingEvaluation(false);
      return;
    }

    // Require "Reviewed With Mariner" for junior officer evaluations before approval
    if (
      props.juniorOfficer &&
      approve === true &&
      evaluationFromApi?.permissions?.assessor2 &&
      EvaluationStatus.ASSESSOR_1_SUBMIT === evaluationFromApi?.status &&
      !values.reviewedWithMariner
    ) {
      toast.error(
        <p>
          You must confirm that you have reviewed this evaluation with the mariner before approving.
        </p>,
        { position: "top-center" }
      );
      setSubmittingEvaluation(false);
      return;
    }

    if (approve !== false) {
      const saveResult = await updateEvaluation({
        id,
        evaluation: buildUpdatedEvaluation(values),
      });
      if ("error" in saveResult) {
        setSubmittingEvaluation(false);
        return;
      }
    }

    let updateResult;
    let formData: FormData | undefined = new FormData();

    if (approve === false) {
      formData.append("rejectionNote", note);
      updateResult = await rejectEvaluation({ id, note: formData });
    } else {
      switch (evaluationFromApi?.status) {
        case EvaluationStatus.EVALUATION_INITIATED:
        case EvaluationStatus.ASSESSOR_1_EDIT:
        case EvaluationStatus.ASSESSOR_1_SUBMIT:
        case EvaluationStatus.ASSESSOR_2_RETURN:
        case EvaluationStatus.FINAL_REVIEWER_RETURN:
        case EvaluationStatus.ASSESSOR_2_APPROVE:
          if (!signatureFile && !signatureText) {
            toast.error(<p>A signature is required.</p>, {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            });
            setSubmittingEvaluation(false);
            return;
          }
          if (signatureFile) {
            const signatureBlob = await (await fetch(signatureFile)).blob();
            formData.append("fileSignature", signatureBlob, "signature.png");
          }
          if (signatureText) {
            formData.append("writtenSignature", signatureText);
          }
          break;
        default:
          formData = undefined;
          break;
      }
      if (approve === true) {
        updateResult = await approveEvaluation({ id, file: formData });
      } else {
        updateResult = await submitEvaluation({ id, file: formData });
      }
    }
    if ("error" in updateResult) {
      setSubmittingEvaluation(false);
      return;
    }
    setSubmittingEvaluation(false);
    handleClose();
    refetch();

    if (
      !(
        approve === true &&
        evaluationFromApi?.status === EvaluationStatus.ASSESSOR_1_SUBMIT
      )
    ) {
      toast.success(
        <p>
          Evaluation {id}{" "}
          {approve === true
            ? "approved"
            : approve === false
            ? "rejected"
            : "submitted"}{" "}
          successfully.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  const handleArchiveEvaluation = async () => {
    setSubmittingEvaluation(true);
    // TODO:
    if (!id) {
      setSubmittingEvaluation(false);
      return;
    }
    let updateResult = await archiveEvaluation({ id });
    if ("error" in updateResult) {
      setSubmittingEvaluation(false);
      return;
    }
    setSubmittingEvaluation(false);
    handleClose();

    navigate(-1);
    toast.success(<p>Evaluation archived successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const getReminderEmail = (values: any) => {
    switch (evaluationFromApi?.status) {
      case EvaluationStatus.EVALUATION_INITIATED:
      case EvaluationStatus.ASSESSOR_1_EDIT:
      case EvaluationStatus.ASSESSOR_2_RETURN:
        return values?.assessor1?.email;
      case EvaluationStatus.ASSESSOR_1_SUBMIT:
      case EvaluationStatus.FINAL_REVIEWER_RETURN:
        return values?.assessor2?.email;
      case EvaluationStatus.ASSESSOR_2_APPROVE:
        return values?.finalReviewer?.email;
      case EvaluationStatus.FINAL_REVIEWER_APPROVE:
        return evaluationFromApi.evaluatee?.email;
      default:
        return "";
    }
  };

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const IsFormDirty = () => {
    const { dirty } = useFormikContext();
    usePrompt("Changes will not be saved, would you like to leave?", dirty);
    return <></>;
  };

  function _handleSubmit(values: any, actions: any) {
    if (!id || id === "new") {
      actions.resetForm({ values });
    }
    _submitForm(values, actions);
  }

  const getMarinerProfileName = (profile: MarinerProfile) => {
    return `${profile.firstName} ${profile.lastName} - ${
      profile.mmcId
    } - ${profile.rating?.substring(1)}`;
  };

  // const getMarinerNameAndTitle = (user: User, assessorType: string) => {
  //   if (!!user) {
  //     switch (assessorType) {
  //       case "assessor1":
  //         return !!user.name && !!evaluationFromApi?.assessor1Title
  //           ? `${user.name} - ${evaluationFromApi?.assessor1Title.substring(1)}`
  //           : user.name;
  //       case "assessor2":
  //         return !!user.name && !!evaluationFromApi?.assessor2Title
  //           ? `${user.name} - ${evaluationFromApi?.assessor2Title.substring(1)}`
  //           : user.name;
  //       case "finalReviewer":
  //         return !!user.name && !!evaluationFromApi?.finalReviewerTitle
  //           ? `${user.name} - ${evaluationFromApi?.finalReviewerTitle.substring(
  //               1
  //             )}`
  //           : user.name;
  //       default:
  //         return "";
  //     }
  //   }
  //   return "";
  // };

  // permissions are handled/calculated in the backend
  const isEditable = () => {
    return !!evaluationFromApi?.permissions?.canEdit;
  };

  const isCommentable = () => {
    return !!evaluationFromApi?.permissions?.canComment;
  };

  const isSubmittable = () => {
    return !!evaluationFromApi?.permissions?.canSubmit;
  };

  const isApprovable = () => {
    return !!evaluationFromApi?.permissions?.canApprove;
  };

  const isArchivable = () => {
    return !!evaluationFromApi?.permissions?.canArchive;
  };

  const isSignable = () => {
    return !!evaluationFromApi?.permissions?.canSign;
  };

  const isRemindable = () => {
    return !!evaluationFromApi?.permissions?.canRemind;
  };

  const isScoreable = () => {
    return !!(isEditable() && evaluationFromApi?.permissions?.assessor1);
  }

  const isReassignable = (assessorNumber: number) => {
    if (assessorNumber === 1) {
      return !!evaluationFromApi?.permissions?.canReassignAssessor1;
    } else if (assessorNumber === 2) {
      return !!evaluationFromApi?.permissions?.canReassignAssessor2;
    } else if (assessorNumber === 3) {
      return !!evaluationFromApi?.permissions?.canReassignAssessor3;
    }

    return false;
  }

  const isExportable = () => {
   return userGroup === UserGroup.HUMAN_RESOURCES ||
          userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
          userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
          userGroup === UserGroup.CREWING_MANAGERS ||
          (props.juniorOfficer && (currentDispatch?.rating === JobRating._Master ||
          user.username === evaluationFromApi?.assessor1UserId ||
          user.username === evaluationFromApi?.assessor2UserId)) || 
          (evaluationFromApi?.permissions?.evaluatee &&
            (evaluationFromApi?.status ===
              EvaluationStatus.FINAL_REVIEWER_APPROVE ||
              evaluationFromApi.status ===
                EvaluationStatus.SENIOR_OFFICER_SIGN))
  }
  const availableActions = (isSubmitting: boolean, values: any) => {
    return (
      <>
        {(isEditable() || isCommentable() || isReassignable(1) || isReassignable(2) || isReassignable(3)) && (
          <Button
            variant="contained"
            className="blue-button mr-2"
            type="submit"
            disabled={isSubmitting}
          >
            Save Changes
          </Button>
        )}
        {isArchivable() && (
          <Button
            variant="contained"
            className="blue-button mr-2"
            onClick={() => handleArchiveEvaluation()}
            disabled={submittingEvaluation}
          >
            Archive
          </Button>
        )}
        {isSubmittable() && (
          <Button
            variant="contained"
            className="blue-button mr-2"
            onClick={() => handleSubmitEvaluation(undefined, values)}
            disabled={submittingEvaluation}
          >
            Sign & Submit
          </Button>
        )}
        {isApprovable() && (
          <>
            <Button
              variant="contained"
              className="blue-button mr-2"
              onClick={() => handleSubmitEvaluation(true, values)}
              disabled={submittingEvaluation}
            >
              Sign & Approve
            </Button>
            <Button
              variant="contained"
              className="blue-button mr-2"
              onClick={handleOpen}
              disabled={submittingEvaluation}
            >
              Reject
            </Button>
            <Dialog
              open={confirmReject}
              onClose={handleClose}
              aria-labelledby="modal-title"
            >
              <Typography variant="h3" id="modal-title">
                Reject Evaluation
              </Typography>
              <p className="delete-paragraph">
                Are you sure you would like to reject this Evaluation?
              </p>
              <label>
                <span>Note:</span>
                <textarea
                  id="rejectionNote"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                />
              </label>
              <div className="modal-footer">
                <Button className="outline-button-small " onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  className="blue-button-small mr-2"
                  onClick={() => handleSubmitEvaluation(false)}
                >
                  Reject
                </Button>
              </div>
            </Dialog>
          </>
        )}
        {isSignable() &&
          !!userProfile?.id &&
          userProfile.id === evaluationFromApi?.evaluatee?.id && (
            <Button
              variant="contained"
              className="blue-button mr-2"
              onClick={() => handleUploadSignature(values)}
              disabled={submittingEvaluation}
            >
              Sign & Submit
            </Button>
          )}
        {(userGroup === UserGroup.HUMAN_RESOURCES ||
          userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
          userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
          userGroup === UserGroup.CREWING_MANAGERS ||
          isExportable() ||
          (evaluationFromApi?.permissions?.evaluatee &&
            (evaluationFromApi?.status ===
              EvaluationStatus.FINAL_REVIEWER_APPROVE ||
              evaluationFromApi.status ===
                EvaluationStatus.SENIOR_OFFICER_SIGN))) &&
          !isLoading &&
          !isFetching && (
            <div className="header-group">
              {isRemindable() && (
                <EmailModal
                  key={EmailType.EVALUATION_ALERT}
                  email={getReminderEmail(values)}
                  emailType={EmailType.EVALUATION_ALERT}
                  parentCallback={handleEmailCallBack}
                  emailData={{
                    vesselName: evaluationFromApi?.vessel?.name!,
                    rating:
                      JobRatingLabel[
                        evaluationFromApi?.rating! ||
                          evaluationFromApi?.profile?.rating!
                      ],
                    marinerName: [
                      evaluationFromApi?.profile?.firstName,
                      evaluationFromApi?.profile?.middleName,
                      evaluationFromApi?.profile?.lastName,
                    ].join(" "),
                    evalLink: `${window.location.origin}/evaluations/${evaluationFromApi?.id}`,
                  }}
                  emailSubject={`ACTION: Performance Evaluation for ${[
                    evaluationFromApi?.profile?.firstName,
                    evaluationFromApi?.profile?.middleName,
                    evaluationFromApi?.profile?.lastName,
                  ].join(" ")}`}
                  cc={ccEmail}
                >
                  <Button variant="contained" className="blue-button">
                    <Email className="mr-0" />
                    Remind
                  </Button>
                </EmailModal>
              )}
              <Button
                variant="contained"
                className="blue-button mr-2"
                onClick={handleExport}
              >
                Export
              </Button>
            </div>
          )}
      </>
    );
  };

  // methods for reject dialog
  const handleOpen = () => {
    setConfirmReject(true);
  };

  const handleClose = () => {
    setConfirmReject(false);
  };

  const handleExport = async () => {
    if (!formRef.current) return;

    const fixedWidth = 2056;

      // prep the PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
      // Get PDF dimensions in points
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
      // remove the first, default, blank page
    pdf.deletePage(1);

    const HEADER_HEIGHT_MM = 8;

    await addRefToPdf(formRef.current, pdf, fixedWidth, pdfWidth, pdfHeight, 0, HEADER_HEIGHT_MM);

    // Comment sections always start on a fresh page; currentPageY=0 forces that
    let currentPageY = 0;

    const commentSectionRefs = [
      commentHeaderRef,
      assessor1SectionRef,
      assessor2SectionRef,
      ...(props.juniorOfficer ? [] : [finalReviewerSectionRef]),
      evaluateeSectionRef,
    ];

    for (const ref of commentSectionRefs) {
      if (ref.current) {
        currentPageY = await addRefToPdf(ref.current, pdf, fixedWidth, pdfWidth, pdfHeight, currentPageY, HEADER_HEIGHT_MM);
      }
    }

    await addHeaders(pdf, HEADER_HEIGHT_MM);
    addFooters(pdf);

    pdf.save(
      `evaluation${
        evaluationFromApi?.evaluatee
          ? `_${evaluationFromApi.evaluatee.firstName}${
              evaluationFromApi.evaluatee.middleName ?? ""
            }${evaluationFromApi.evaluatee.lastName}`
          : ""
      }.pdf`
    );
  };

  const addRefToPdf = async (
    ref: HTMLDivElement,
    pdf: jsPDF,
    width: number,
    pdfWidth: number,
    pdfHeight: number,
    currentPageY: number = 0,
    headerHeightMm: number = 0
  ): Promise<number> => {
    return html2canvas(ref, {
      windowWidth: width,
      onclone: (document) => {
        const evalForm = document.getElementById("eval-form");
        if (evalForm) {
          evalForm.style.paddingLeft = "40px";
          evalForm.style.paddingRight = "60px";
          evalForm.style.paddingTop = "40px";
          evalForm.style.width = props.juniorOfficer ? "1850px" : "1750px";
        }

        const commentSection = document.getElementById("comments-section");
        if (commentSection) {
          commentSection.style.paddingLeft = "40px";
          commentSection.style.paddingRight = "60px";
          commentSection.style.paddingTop = "40px";
          commentSection.style.width = "1850px";
        }

        // Apply matching margins to individually-rendered comment sections
        document.querySelectorAll("[data-pdf-section]").forEach((el) => {
          const section = el as HTMLElement;
          section.style.paddingLeft = "40px";
          section.style.paddingRight = "60px";
          section.style.width = "1850px";
          if (section.dataset.pdfSection === "header") {
            section.style.paddingTop = "40px";
          }
        });

        // Logos are added as a jsPDF overlay on every page via addHeaders instead

        const evalScoreSection = document.getElementById("Score-section");

        if (evalScoreSection) {
          // Temporarily expand to capture full content
          evalScoreSection.style.overflow = "visible";
          evalScoreSection.style.height = "auto"; // Expand to fit content
        }

        const evalSection = document.getElementById("eval-section");

        if (evalSection) {
          evalSection.style.height = "auto";
        }

        // convert textareas to divs since html2canvas can't properly capture multi-line textarea
        const textareas = document.querySelectorAll("textarea");

        textareas.forEach((textarea) => {
          if (textarea.ariaHidden === "true") {
            textarea.setAttribute("data-html2canvas-ignore", "true");
          } else {
            const computedStyle = window.getComputedStyle(textarea);

            const div = document.createElement("div");
            div.textContent = textarea.value;
            div.style.cssText = `
                  width: ${textarea.clientWidth}px;
                  min-height: ${textarea.clientHeight}px;
                  max-height: 200px;
                  overflow:hidden;
                  border: ${computedStyle.border};
                  padding: ${computedStyle.padding};
                  font-family: ${computedStyle.fontFamily};
                  font-size: ${computedStyle.fontSize};
                  line-height: ${computedStyle.lineHeight};
                  color: ${computedStyle.color};
                  background: ${computedStyle.background};
                  white-space: pre-wrap;  /* Preserve line breaks */
                  word-wrap: break-word;  /* Ensure long words break */
                `;
            textarea.replaceWith(div);
          }
        });

        // Fix for sticky elements
        const fixedElements = document.querySelectorAll(
          ".sticky, .off, .mariner, .rating"
        );

        fixedElements.forEach((el) => {
          el.classList.remove("sticky", "off", "mariner", "rating");
        });
      },
      scale: 2,
      allowTaint: false,
    }).then((canvas) => {
      const leftMargin = 50;

      const canvasWidth = canvas.width - leftMargin;
      const canvasHeight = canvas.height;
      const pxPerMm = canvasWidth / pdfWidth;
      const contentHeightMm = canvasHeight / pxPerMm;

      // Usable page height after reserving space for the logo header
      const usablePdfHeight = pdfHeight - headerHeightMm;
      const pageHeight = (usablePdfHeight * canvasWidth) / pdfWidth;

      const findBreakPoint = (startY: number): number => {
        const remaining = canvasHeight - startY;
        if (remaining <= pageHeight) return remaining;

        const ctx = canvas.getContext("2d");
        if (!ctx) return pageHeight;

        const maxY = Math.min(startY + Math.floor(pageHeight), canvasHeight);
        const minY = Math.max(startY, maxY - Math.floor(pageHeight * 0.5));

        for (let y = maxY; y >= minY; y--) {
          const data = ctx.getImageData(0, y, canvasWidth, 1).data;
          let isBackground = true;
          for (let x = 0; x < data.length; x += 40) {
            if (data[x] < 200 || data[x + 1] < 200 || data[x + 2] < 200) {
              isBackground = false;
              break;
            }
          }
          if (isBackground) return y - startY;
        }
        return pageHeight;
      };

      // If the content fits in the remaining space on the current page, place it inline
      if (currentPageY > 0 && contentHeightMm <= pdfHeight - currentPageY) {
        const croppedCanvas = document.createElement("canvas");
        const croppedCtx = croppedCanvas.getContext("2d");
        croppedCanvas.width = canvasWidth;
        croppedCanvas.height = canvasHeight;
        croppedCtx?.drawImage(canvas, 0, 0, canvasWidth, canvasHeight, 0, 0, canvasWidth, canvasHeight);
        const imgHeight = (pdfWidth * canvasHeight) / canvasWidth;
        pdf.addImage(croppedCanvas.toDataURL("image/png"), "PNG", 0, currentPageY, pdfWidth, imgHeight);
        return currentPageY + imgHeight;
      }

      // Otherwise slice across new pages, each offset below the logo header
      let yPosition = 0;
      let lastSliceHeightPx = pageHeight;

      while (yPosition < canvasHeight) {
        const sliceHeight = findBreakPoint(yPosition);
        lastSliceHeightPx = sliceHeight;

        const pageCanvas = document.createElement("canvas");
        const pageCtx = pageCanvas.getContext("2d");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sliceHeight;

        if (!pageCtx) {
          console.error("Failed to get 2D context for page canvas.");
          return 0;
        }

        pageCtx.drawImage(canvas, 0, yPosition, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight);
        const pageImgData = pageCanvas.toDataURL("image/png");

        pdf.addPage();
        pdf.addImage(pageImgData, "PNG", 0, headerHeightMm, pdfWidth, (pdfWidth * sliceHeight) / canvasWidth);

        yPosition += sliceHeight;
      }

      // Return how much of the last page was used
      return headerHeightMm + lastSliceHeightPx / pxPerMm;
    });
  };

  const addHeaders = async (doc: jsPDF, headerHeightMm: number) => {
    const pageCount = doc.internal.pages.length - 1;
    const img = new Image();
    img.src = logo;
    await new Promise<void>((resolve) => { img.onload = () => resolve(); });
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const logoH = headerHeightMm - 2;
    const logoW = logoH * aspectRatio;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.addImage(img, "PNG", 5, 3, logoW, logoH);
    }
  };

  const addFooters = (doc: jsPDF) => {
    // account for the empty first page
    const pageCount = doc.internal.pages.length - 1;
    const footerText = props.juniorOfficer ? "Junior Officer Evaluation" : "Performance Evaluation " + getEvaluationYear();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("Helvetica", "Bold");
      doc.setFontSize(9);
      doc.setTextColor(15, 46, 105);
      doc.text(footerText, 10, 290, {
        align: "left",
      });
      doc.text(
        String(i) + " of " + String(pageCount),
        doc.internal.pageSize.width - 20,
        290,
        {
          align: "right",
        }
      );
    }
  };

  const getEvaluationYear = () => {
    const evalYear = evaluationFromApi?.fromDate?.substring(0, 4);
    return !!evalYear ? evalYear : "2024"; // what should default year be?
  };

  if (userGroup === UserGroup.MARINERS && !userProfile) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderSignatureIfPresent = (signatureFromApi: EvaluationSignature) => {
    if (!signatureFromApi) {
      return null;
    }

    return (
      <div className="flex-col">
        <p className="b-600">Signature</p>{" "}
        {signatureFromApi.imageBase64 && (
          <img
            alt="preview"
            width="100%"
            className="border p-2"
            src={signatureFromApi.imageBase64}
          />
        )}
        {signatureFromApi.signatureText && (
          <p
            style={{
              fontFamily: "cursive",
              fontSize: "40px",
            }}
          >
            {signatureFromApi.signatureText}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
      >
        {({
          isSubmitting,
          values,
          initialValues,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          errors,
          touched,
          status,
        }) => (
          <Form id={formId} style={{ marginTop: "-30px" }}>
            <div ref={formRef} id="eval-form" className="eval-form">
              <IsFormDirty />
              <div className="page-header bg-white align-items-center sticky justify-content-btwn mb-0 pb-2 pt-3">
                <div className="flex g-3 align-items-center">
                  <img
                    className="pdf-logo-size"
                    id="pdf-logo"
                    src={logo}
                    alt=""
                  />
                  <h1>{props.juniorOfficer ? `Junior Officer Evaluation` : `Senior Officer Evaluation`}</h1>
                  <div
                    data-html2canvas-ignore
                    className="flex align-items-center p-2"
                  >
                    {availableActions(isSubmitting, values)}
                  </div>
                  <div data-html2canvas-ignore>
                    <MatrixModal evaluationType={props.juniorOfficer ? 'junior' : 'senior'}/>
                  </div>
                </div>

                <Button
                  data-html2canvas-ignore
                  className="blue-link-button"
                  onClick={() => navigate(-1)}
                >
                  {" "}
                  <ArrowBack />
                  Back
                </Button>
              </div>
              {isSubmitting && <CircularProgress size={24} />}
              <Paper>
                <div className="registration-header mb-1">
                  <h3 className="ft-18">Mariner Information</h3>
                </div>
                <div className="form-wide">
                  <label>
                    <span className="required">{mmcId.label}</span>
                    <InputField
                      name={mmcId.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={true}
                    />
                  </label>
                  <label>
                    <span className="required">{firstName.label}</span>
                    <InputField
                      name={firstName.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={true}
                    />
                  </label>
                  <label>
                    <span>{middleName.label}</span>
                    <InputField
                      name={middleName.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={true}
                    />
                  </label>
                  <label>
                    <span className="required">{lastName.label}</span>
                    <InputField
                      name={lastName.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={true}
                    />
                  </label>
                  <label>
                    <span>{rating.label}</span>
                    <InputField
                      name={rating.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={true}
                      value={
                        JobRatingLabel[
                          JobRating[values.rating as keyof typeof JobRating]
                        ] ?? ""
                      }
                    />
                  </label>
                  <label>
                    <span className="required">{vessel.label}</span>
                    <InputField
                      name={vessel.name}
                      value={(values.vessel as Vessel)?.name ?? ""}
                      disabled={true}
                    />
                  </label>
                  <label>
                    <span>{fromDate.label}</span>
                    <InputField
                      name={fromDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled={!isEditable()}
                    />
                  </label>
                  <label>
                    <span>{toDate.label}</span>
                    <InputField
                      name={toDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled={!isEditable()}
                    />
                  </label>
                </div>
                {!props.juniorOfficer && 
                  <div>
                    <div className="registration-header pt-2 mb-1">
                      <h3 className="ft-18">Vessel Safety Statistics</h3>
                    </div>
                    <div>
                      <div className="form-wide">
                        <label>
                          <span>{nearMissSubmissions.label}</span>
                          <span>{values.nearMissSubmissions ?? 0}</span>
                        </label>
                        <label>
                          <span>{lostTimeInjuries.label}</span>
                          <span>{values.lostTimeInjuries ?? 0}</span>
                        </label>
                        <label>
                          <span>{restrictedWorkInjuries.label}</span>
                          <span>{values.restrictedWorkInjuries ?? 0}</span>
                        </label>
                        <label>
                          <span>{medicalTreatmentInjuries.label}</span>
                          <span>{values.medicalTreatmentInjuries ?? 0}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                }
              </Paper>
              <div className="eval-section" id="eval-section">
                <Paper className="analysis-desc">
                  <div className="registration-header">
                    <h3 className="ft-18">Analysis of Performance Scores</h3>
                  </div>
                  <div className="flex-col g-1">
                    <div className="pb-2 border-bottom-light">
                      <p className="ft-14 mb-1">
                        This section is designed to serve as a constructive
                        guide for analyzing the employee's performance. Read
                        each category carefully and consider the extent to which
                        the employee demonstrates the element of performance
                        described. Indicate your analysis by marking a{" "}
                        <b>score 1-5.</b>
                      </p>
                      <p className="ft-14 mb-1">
                        Fractional point scores <b>(in .5 increments) </b> can{" "}
                        be used to refine assessment, i.e. 3.5 = Satisfactory
                        but short of Exceeds.
                      </p>
                      <p className="ft-14 mb-1">
                        Assessors should seek inputs from other areas in order
                        to properly evaluate areas pertaining to safety,
                        regulatory compliance and administrative skills.
                      </p>
                      <p className="ft-14">
                        <b>Comments should include examples </b> pertaining to
                        the individual’s level of commitment towards fostering a
                        safety culture.
                      </p>
                    </div>
                    {/* TODO: */}
                    <div className="flex-col g-0">
                      <h4>1 - Unsatisfactory</h4>
                      <small className="ft-14">
                        Does not meet position requirements, objectives and
                        expectations on the vessel. Immediate attention to
                        improvement and/or action is required.
                      </small>
                    </div>

                    <div className="flex-col g-0">
                      <h4>2 - Needs Improvement</h4>
                      <small className="ft-14">
                        Meets some position requirements, objectives and
                        expectations on the vessel. Attention to improvement is
                        needed.
                      </small>
                    </div>
                    <div className="flex-col g-0">
                      <h4>3 - Meets Expectation</h4>
                      <small className="ft-14">
                        Meets all of the position requirements on the vessel.
                      </small>
                    </div>
                    <div className="flex-col g-0">
                      <h4>4 - Exceeds </h4>
                      <small className="ft-14">
                        Exceeds normal job requirements on the vessel.
                      </small>
                    </div>
                    <div className="flex-col g-0">
                      <h4>5 - Greatly Exceeds</h4>
                      <small className="ft-14">
                        Exceeds normal job requirements and is making an impact
                        not only aboard their vessel but is leading improvement
                        fleetwide.
                      </small>
                    </div>
                  </div>
                </Paper>

                <FieldArray
                  name="scores"
                  render={(arrayHelpers) => (
                    <Paper>
                      <div
                        id="Score-section"
                        style={{ overflowY: "auto", height: "710px" }}
                      >
                        <div
                          className="registration-header"
                          style={{ marginBottom: "0px" }}
                        >
                          <h3 className="ft-18">
                            Performance Evaluation Scores
                          </h3>
                        </div>
                        {values.scores &&
                          values.scores.length > 0 &&
                          // reduce the scores to group them by subsection, then loop over subsections and create html elements
                          Object.entries(
                            (values.scores as EvaluationScore[]).reduce(
                              (
                                acc: {
                                  [key: string]: {
                                    score: EvaluationScore;
                                    originalIndex: number;
                                  }[];
                                },
                                curr: EvaluationScore,
                                originalIndex: number
                              ) => {
                                const subSection =
                                  curr.question?.subSection || "";
                                if (!acc[subSection]) {
                                  acc[subSection] = [];
                                }
                                acc[subSection].push({
                                  score: curr,
                                  originalIndex,
                                });
                                return acc;
                              },
                              {}
                            )
                          ).map(([subSection, subSectionScores]) => (
                            <div key={subSection}>
                              <h3 className="sub-reg-header">{subSection}</h3>
                              {subSectionScores.map(
                                ({ score, originalIndex }) => (
                                  <div
                                    className="score-rows"
                                    key={originalIndex}
                                    style={{
                                      display: "grid",
                                      gap: "16px",
                                      alignItems: "center",
                                      gridTemplateColumns: "1fr 100px",
                                    }}
                                  >
                                    <div>
                                      <p className="b-600">
                                        {score.question?.questionText}
                                      </p>
                                      <small className="ft-14">
                                        {score.question?.description}
                                      </small>
                                    </div>
                                    <div className="flex-row align-items-end g-1">
                                      <InputField
                                        className="eval-score"
                                        name={`scores.${originalIndex}.score`}
                                        type="text"
                                        step={0.5}
                                        min={1}
                                        max={5}
                                        validate={(value: number) => {
                                          if (
                                            !!value &&
                                            (value < 1 ||
                                              value > 5 ||
                                              (value * 10) % 5 !== 0)
                                          ) {
                                            return "Score must be between 1 and 5, in increments of 0.5.";
                                          }
                                        }}
                                        disabled={!isScoreable() || score.notApplicable}
                                      />
                                      <div className="flex-col align-items-center">
                                        <span className="text ft-12">N/A</span>
                                        <Checkbox
                                          className="eval-na-checkbox"
                                          checked={!!score?.notApplicable}
                                          onChange={() => {
                                            arrayHelpers.form.setFieldValue(
                                              `scores.${originalIndex}.notApplicable`,
                                              !score.notApplicable
                                            );
                                            arrayHelpers.form.setFieldValue(
                                              `scores.${originalIndex}.score`,
                                              ''
                                            );
                                          }}
                                          size="small"
                                          disabled={!isScoreable()}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          ))}
                        {props.juniorOfficer && (
                          <div className="flex-col g-1 pt-3 border-top-light">
                            <div className="score-rows" style={{ display: "grid", gap: "16px", alignItems: "center", gridTemplateColumns: "1fr 100px" }}>
                              <div>
                                <p className="b-600">Ready for Promotion?</p>
                              </div>
                              <SelectField
                                name={readyForPromotion.name}
                                data={[
                                  { value: true, label: "Yes" },
                                  { value: false, label: "No" },
                                ]}
                                fullWidth
                                size="small"
                                disabled={!isScoreable()}
                              />
                            </div>
                            <div className="score-rows" style={{ display: "grid", gap: "16px", alignItems: "center", gridTemplateColumns: "1fr 100px" }}>
                              <div>
                                <p className="b-600">Previous warning(s) issued?</p>
                              </div>
                              <SelectField
                                name={hasPreviousWarnings.name}
                                data={[
                                  { value: true, label: "Yes" },
                                  { value: false, label: "No" },
                                ]}
                                fullWidth
                                size="small"
                                disabled={!isScoreable()}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <h2 className="pt-2 flex justify-content-end pr-4">
                        {/* TODO: should this only include non-null scores? */}
                        Performance Grade Average:{" "}
                        {
                          (
                            (values.scores as EvaluationScore[]).filter((s) => !s.notApplicable).reduce(
                              (sum, curr) =>
                                sum +
                                (!!Number(curr.score) ? Number(curr.score) : 0),
                              0
                            ) / (values.scores as EvaluationScore[]).filter((s) => !s.notApplicable).length
                          )
                            .toString()
                            .match(/^-?\d+(?:\.\d{0,2})?/)?.[0]
                        }
                      </h2>
                    </Paper>
                      )}
                />
              </div>
            </div>

            <Paper>
              <div ref={commentRef} id="comments-section">
                <div ref={commentHeaderRef} data-pdf-section="header" className="registration-header flex-col align-items-start g-5px">
                  <div className="flex g-3 align-items-center">
                    <img
                      className="pdf-logo-size"
                      id="pdf-logo-comments"
                      src={logo}
                      alt=""
                    />
                    <h2 className="ft-20">Comments</h2>
                  </div>
                  <p>
                    *Comments are mandatory for any criteria marked 1
                    (Unsatisfactory) and 5 (Greatly Exceeds)
                  </p>
                </div>
                <div className="flex-col">
                  <div ref={assessor1SectionRef} data-pdf-section="section" className="flex-col g-1 py-2 border-bottom-light">
                    <h2 className="ft-20">Assessor 1</h2>
                    <label className="signature-wrapper">
                      <span className="required">{assessor1.label}</span>
                      <EvaluationAssessorAutocomplete
                        id="assessor1.id"
                        vesselId={evaluationFromApi?.vessel?.id || 0}
                        value={{
                          userId: values?.assessor1?.id || "",
                          name: values?.assessor1?.name || "",
                          rating: values?.assessor1?.title || "",
                          email: values?.assessor1?.email || "",
                          mmcId: null
                        }}
                        disabled={evaluationFromApi === undefined || !isReassignable(1)}
                        onChange={(m) => {
                          setFieldValue("assessor1", {
                            id: m?.userId || undefined,
                            name: m?.name || undefined,
                            title: m?.rating || undefined,
                            email: m?.email || undefined
                          });
                        }}
                      />
                    </label>
                    {evaluationFromApi?.permissions?.assessor1 &&
                    evaluationStatusIs([
                      EvaluationStatus.EVALUATION_INITIATED,
                      EvaluationStatus.ASSESSOR_1_EDIT,
                      EvaluationStatus.ASSESSOR_2_RETURN,
                    ]) ? (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {isSignable() && (
                            <EvaluationSignatureInput
                              signatureFile={signatureFile}
                              setSignatureFile={setSignatureFile}
                              signatureText={signatureText}
                              setSignatureText={setSignatureText}
                            />
                          )}
                        </div>
                        <label>
                          <span className="required">
                            {assessor1Comment.label}
                          </span>
                          <EvaluationCommentEditor
                            initialValue={initialValues.assessor1Comment}
                            labelName={assessor1Comment.name}
                            labelHelperName={assessor1CommentHelper.name}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                            errors={errors[assessor1CommentHelper.name]}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {!!evaluationFromApi?.assessor1Signature &&
                            renderSignatureIfPresent(
                              evaluationFromApi.assessor1Signature
                            )}
                        </div>
                        <label>
                          <span>{assessor1Comment.label}</span>
                          <div>
                            <p
                              className="comment-ft-container"
                              dangerouslySetInnerHTML={{
                                __html: values.assessor1Comment,
                              }}
                            />
                          </div>
                        </label>
                      </div>
                    )}
                  </div>

                  <div ref={assessor2SectionRef} data-pdf-section="section" className="flex-col g-1 py-2 border-bottom-light">
                    <h2 className="ft-20">Assessor 2</h2>
                    <label className="signature-wrapper">
                      <span className="required">{assessor2.label}</span>
                      <EvaluationAssessorAutocomplete
                        id="assessor2.id"
                        vesselId={evaluationFromApi?.vessel?.id || 0}
                        value={{
                          userId: values?.assessor2?.id || "",
                          name: values?.assessor2?.name || "",
                          rating: values?.assessor2?.title || "",
                          email: values?.assessor2?.email || "",
                          mmcId: null
                        }}
                        disabled={evaluationFromApi === undefined || !isReassignable(2)}
                        onChange={(m) => {
                          setFieldValue("assessor2", {
                            id: m?.userId || undefined,
                            name: m?.name || undefined,
                            title: m?.rating || undefined,
                            email: m?.email || undefined,
                          });
                        }}
                      />
                    </label>
                    {evaluationFromApi?.permissions?.assessor2 &&
                    evaluationStatusIs([
                      EvaluationStatus.ASSESSOR_1_SUBMIT,
                      EvaluationStatus.FINAL_REVIEWER_RETURN,
                    ]) ? (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {isSignable() && (
                            <EvaluationSignatureInput
                              signatureFile={signatureFile}
                              setSignatureFile={setSignatureFile}
                              signatureText={signatureText}
                              setSignatureText={setSignatureText}
                            />
                          )}
                        </div>
                        <label>
                          <span>{assessor2Comment.label}</span>
                          <EvaluationCommentEditor
                            initialValue={initialValues.assessor2Comment}
                            labelName={assessor2Comment.name}
                            labelHelperName={assessor2CommentHelper.name}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                          />
                        </label>

                        {props.juniorOfficer && (
                          <div className="reviewed-with-mariner-container">
                            <input
                              type="checkbox"
                              id="reviewedWithMariner"
                              name="reviewedWithMariner"
                              checked={!!values.reviewedWithMariner}
                              onChange={(e) => {
                                setFieldValue("reviewedWithMariner", e.target.checked);
                                setFieldTouched("reviewedWithMariner", true, false);
                              }}
                              disabled={!isEditable() && !evaluationFromApi?.permissions?.assessor2}
                              className="reviewed-with-mariner-checkbox"
                            />
                            <div className="reviewed-with-mariner-text">
                              <span className="b-600">Reviewed With Mariner</span>
                              <small className="ft-14">
                                Please confirm that you have reviewed this evaluation with the mariner.
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {!!evaluationFromApi?.assessor2Signature &&
                            renderSignatureIfPresent(
                              evaluationFromApi.assessor2Signature
                            )}
                        </div>
                        <label>
                          <span>{assessor2Comment.label}</span>
                          <div>
                            <p
                              className="comment-ft-container"
                              dangerouslySetInnerHTML={{
                                __html: values.assessor2Comment,
                              }}
                            />
                          </div>
                        </label>

                        {props.juniorOfficer && (
                          <div className="reviewed-with-mariner-container">
                            <div className="reviewed-with-mariner-text">
                              <span className="b-600">Reviewed With Mariner</span>
                              <small className="ft-14">
                                Please confirm that you have reviewed this evaluation with the mariner.
                              </small>
                            </div>
                            <input
                              type="checkbox"
                              id="reviewedWithMariner"
                              name="reviewedWithMariner"
                              checked={!!evaluationFromApi?.reviewedWithMariner}
                              readOnly
                              className="reviewed-with-mariner-checkbox"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {!props.juniorOfficer && <div ref={finalReviewerSectionRef} data-pdf-section="section" className="flex-col g-1 py-2 border-bottom-light">
                    <h2 className="ft-20">Final Reviewer</h2>
                    <label className="signature-wrapper">
                      <span className="required">{finalReviewer.label}</span>
                      <EvaluationAssessorAutocomplete
                        id="finalReviewer.id"
                        vesselId={evaluationFromApi?.vessel?.id || 0}
                        value={{
                          userId: values?.finalReviewer?.id || "",
                          name: values?.finalReviewer?.name || "",
                          rating: values?.finalReviewer?.title || "",
                          email: values?.finalReviewer?.email || "",
                          mmcId: null
                        }}
                        disabled={evaluationFromApi === undefined || !isReassignable(3)}
                        onChange={(m) => {
                          setFieldValue("finalReviewer", {
                            id: m?.userId || undefined,
                            name: m?.name || undefined,
                            title: m?.rating || undefined,
                            email: m?.email || undefined
                          });
                        }}
                      />
                    </label>
                    {evaluationFromApi?.permissions?.finalReviewer &&
                    evaluationStatusIs([
                      EvaluationStatus.ASSESSOR_2_APPROVE,
                    ]) ? (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {isSignable() && (
                            <EvaluationSignatureInput
                              signatureFile={signatureFile}
                              setSignatureFile={setSignatureFile}
                              signatureText={signatureText}
                              setSignatureText={setSignatureText}
                            />
                          )}
                        </div>
                        <label>
                          <span>{finalReviewerComment.label}</span>
                          <EvaluationCommentEditor
                            initialValue={initialValues.finalReviewerComment}
                            labelName={finalReviewerComment.name}
                            labelHelperName={finalReviewerCommentHelper.name}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className=" flex-col g-1">
                        <div className="signature-wrapper">
                          {!!evaluationFromApi?.finalReviewerSignature &&
                            renderSignatureIfPresent(
                              evaluationFromApi.finalReviewerSignature
                            )}
                        </div>
                        <label>
                          <span>{finalReviewerComment.label}</span>
                          <div>
                            <p
                              className="comment-ft-container"
                              dangerouslySetInnerHTML={{
                                __html: values.finalReviewerComment,
                              }}
                            />
                          </div>
                        </label>
                      </div>
                    )}
                  </div>}

                  <div ref={evaluateeSectionRef} data-pdf-section="section" className="flex-col g-1 py-2">
                    <h2 className="ft-20">{props.juniorOfficer ? `Junior Officer`: `Senior Officer`}</h2>
                    <label className="signature-wrapper">
                      <span>{props.juniorOfficer ? 'Junior Officer - Full Name' : evaluatee.label}</span>
                      <InputField
                        name={evaluatee.name}
                        size="small"
                        type="text"
                        disabled={true}
                        value={
                          !!values.evaluatee
                            ? getMarinerProfileName(
                                values.evaluatee as MarinerProfile
                              )
                            : ""
                        }
                      />
                    </label>
                    {evaluationFromApi?.permissions?.evaluatee &&
                    evaluationStatusIs([
                      EvaluationStatus.FINAL_REVIEWER_APPROVE,
                    ]) ? (
                      <div className="flex-col g-1">
                        <div className="signature-wrapper">
                          {isSignable() && (
                            <EvaluationSignatureInput
                              signatureFile={signatureFile}
                              setSignatureFile={setSignatureFile}
                              signatureText={signatureText}
                              setSignatureText={setSignatureText}
                            />
                          )}
                        </div>
                        <label>
                          <span>{evaluateeComment.label}</span>
                          <EvaluationCommentEditor
                            initialValue={initialValues.evaluateeComment}
                            labelName={evaluateeComment.name}
                            labelHelperName={evaluateeCommentHelper.name}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className=" flex-col g-1">
                        <div className="signature-wrapper">
                          {!!evaluationFromApi?.evaluateeSignature &&
                            renderSignatureIfPresent(
                              evaluationFromApi.evaluateeSignature
                            )}
                        </div>
                        <label>
                          <span>{evaluateeComment.label}</span>
                          <div>
                            <p
                              className="comment-ft-container"
                              dangerouslySetInnerHTML={{
                                __html: values.evaluateeComment,
                              }}
                            />
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Paper>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EvaluationForm;
